import type { ButtonPriority } from '@timelyapp/react';
import { Checkbox, PopoverConfirm } from '@timelyapp/react';
import classNames from 'classnames';
import DropdownList from 'components/DropdownList';
import type { OptionTypes } from 'components/DropdownListItem';
import DropdownListItem from 'components/DropdownListItem';
import { InfoIcon } from 'components/Icons';
import React, { Component } from 'react';
import FontAwesome from 'react-fontawesome';
import type { Merge } from 'types/utility';
import styles from './DropdownList.module.scss';
import Input from './Input';
import InputDecorator from './InputDecorator';
import { Radio } from './Radio';
import RichTooltip from './RichTooltip';

export interface DropdownOption<TMeta = any> {
  /** Optional id reference on this option (typically needed when an `action` is shared across options, but the options still need identifiers) */
  idRef?: number;
  /** Action name, typically used in generic `onClick` handlers */
  action?: string;
  /** If true a new Popover with confirmation is rendered and onClick is not invoked. */
  confirm?: boolean;
  /** Pass the FontAwesome name to add icon */
  faIcon?: string;
  /** Primary name for the option */
  name?: string;
  /** Render as a visual separator instead of a clickable option */
  separator?: boolean;
  /** Newline, muted description */
  description?: string | React.ReactNode;
  /** Show option, but disallow clicks */
  disabled?: boolean;
  /** Control the visual style of this option */
  type?: OptionTypes;
  /** Use a custom SVG icon here (see also `faIcon`) */
  icon?: React.ReactElement;
  /** Display as active option */
  active?: boolean;
  /** Display as indeterminate option when `checkbox` is true */
  indeterminate?: boolean;
  /** Optionally pass the message to display when `confirm` is used */
  confirmMessage?: React.ReactNode;
  /** Show "upgrade star" on this option */
  isLockedFeature?: boolean;
  /** Show "experimental star" on this option */
  isExperimentalFeature?: boolean;
  /** Show a tooltip when hovering this option? */
  tooltip?: string | React.ReactElement;
  /** Display this with a checkbox prefixed (should not be combined with fa/icon) */
  checkbox?: boolean;
  /** Display this with a radio button prefixed (should not be combined with fa/icon) */
  radio?: boolean;
  /** Custom metadata that can be used for e.g. filtering */
  meta?: TMeta;
  /** Optionally perform an action if we try to click an item that is disabled due to being locked */
  onLockedFeatureClick?: () => void;
  /** Display this as a text input field */
  textbox?: boolean;
  /** Content of the textbox if `textbox` is `true` */
  textboxContent?: string;
  /** `onChange` handler for the textbox if `textbox` is `true` */
  onTextboxChange?: (value: string) => void;
}

interface OwnProps<T extends DropdownOption> {
  /** How much padding on the container, in px */
  padding?: number;
  /** List of options that will be displayed */
  options: Readonly<Array<T>>;
  /** This will be called when an option is clicked */
  onClick: (option: T, event: React.SyntheticEvent) => unknown;
  confirmButtonPriority?: ButtonPriority;
}

type Props<T extends DropdownOption> = Merge<React.HTMLProps<HTMLUListElement>, OwnProps<T>>;

export default class DropdownListWithOptions<T extends DropdownOption> extends Component<Props<T>> {
  static defaultProps = {
    options: [],
    padding: 8,
    onClick: () => {},
  };

  handleTextboxChange = (option: DropdownOption<T>, event: React.FormEvent<HTMLInputElement>) => {
    option.onTextboxChange?.((event.target as HTMLInputElement).value);
  };

  handleTextboxClear = (option: DropdownOption<T>) => {
    option.onTextboxChange?.('');
  };

  renderOptions(): Array<React.ReactNode> {
    return this.props.options.map((option, index) => {
      const target = (
        <>
          {option.checkbox && (
            <span className={styles.checkbox}>
              <Checkbox
                readOnly
                disabled={option.disabled}
                checked={option.active}
                indeterminate={option.indeterminate}
              />
            </span>
          )}
          {option.radio && (
            <span className={styles.radio}>
              <Radio
                readOnly
                value="undefined"
                disabled={option.disabled}
                checked={option.active}
              />
            </span>
          )}
          {option.faIcon && (
            <span className={styles.faIcon}>
              <FontAwesome name={option.faIcon} />
            </span>
          )}
          {option.icon && (
            <span
              className={classNames(styles.svgIcon, {
                [styles.withDescription]: !!option.description,
              })}
            >
              {option.icon}
            </span>
          )}
          {option.description ? (
            <div className={styles.description}>
              {option.name}
              <small>{option.description}</small>
            </div>
          ) : (
            option.name
          )}
          {option.type === 'search' && (
            <InputDecorator
              size="medium"
              faIcon="search"
              onClear={() => this.handleTextboxClear(option)}
              isClearable
              hideClearButton={!option.textboxContent}
            >
              <Input
                value={option.textboxContent}
                onChange={event => this.handleTextboxChange(option, event)}
              />
            </InputDecorator>
          )}
        </>
      );

      const onClick = (event: React.SyntheticEvent<HTMLElement>) => {
        if (option.confirm) {
          return;
        }
        this.props.onClick(option, event);
      };

      const renderListItemContent = () => {
        if (option.type === 'header') {
          return option.name;
        }
        if (option.type === 'info') {
          return (
            <div className={styles.displayOptionFooter}>
              <InfoIcon width={14} height={14} />
              <p className={styles.infoText}>{option.description}</p>
            </div>
          );
        }

        return target;
      };

      let item = (
        <DropdownListItem
          key={index}
          type={option.type}
          isDisabled={option.disabled}
          isActive={option.active && !option.radio && !option.checkbox}
          isLockedFeature={option.isLockedFeature}
          isExperimentalFeature={option.isExperimentalFeature}
          onLockedFeatureClick={option.onLockedFeatureClick}
          onClick={onClick}
        >
          {renderListItemContent()}
        </DropdownListItem>
      );

      if (option.confirm) {
        item = (
          <PopoverConfirm
            target={item}
            targetWrapper={false}
            onConfirm={event => this.props.onClick(option, event)}
            prompt={option.confirmMessage || 'Are you sure?'}
            closeOnClick={false}
            confirmButtonPriority={this.props.confirmButtonPriority}
            confirmButtonDestructive={option.action === 'delete'}
          />
        );
      }

      const testId = (option.name || '__anonymous')
        .toLowerCase()
        .replace(/ /g, '_')
        .concat('_dropdown_list_options');

      if (option.tooltip) {
        item = (
          <RichTooltip data-testid={testId} tooltip={option.tooltip}>
            {item}
          </RichTooltip>
        );
      }

      const key = `${option.action || option.name || option.type}-${index}`;
      return (
        <span key={key} data-testid={testId}>
          {item}
        </span>
      );
    });
  }

  render() {
    const { options, onClick, confirmButtonPriority, ...rest } = this.props;
    return <DropdownList {...rest}>{this.renderOptions()}</DropdownList>;
  }
}
